import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DistributorService } from '../../../../distributor.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { IDetailVerificationPlafonKredit, IPayloadVerificationPlafonKredit } from '../../../../interfaces/detail-verification.interface';
import { ICardVerificationDocument, ICardVerificationForm, INoteChangeVerification } from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../../detail-registration/detail-registration.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { UtilitiesService } from '@services/utilities.service';
import {
  creditBillingBodyData,
  creditCeilingBodyData,
  EnumCardVerificationForm,
  EnumCardVerificationSection,
  saleTargetBodyData,
} from '../../detail-registration/distributor-data/distributor-data.data';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { IDetailPlafonKredit_CreditCeiling_BillDetails } from '../../../../interfaces/detail-plafon-kredit.interface';
import { EnumVerificationStatus } from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';
import { UrlUtilsService } from '@utils/url-utils.service';
import { DistributorDetailService } from '../../../../distributor-detail.service';

enum EnumLabelKeyFormString {
  credit_ceiling_granted = 'Plafon Kredit',
  sale_target = 'Target Penjualan',
  credit_billing = 'Penagihan Kredit',
}

@Component({
  selector: 'app-edit-request-plafon',
  templateUrl: './edit-request-tab-plafon-kredit.component.html',
  styleUrls: ['./edit-request-tab-plafon-kredit.component.scss'],
})
export class EditRequestTabPlafonKreditComponent implements OnInit {
  isLoading = new BehaviorSubject<boolean>(false);
  snapshotParams!: Params;

  revisionNoteData!: IPayloadVerificationPlafonKredit;
  responseSubject = new BehaviorSubject({} as IDetailVerificationPlafonKredit);

  creditCeilingSubject = new BehaviorSubject([] as ICardVerificationForm[]);
  creditBillingSubject = new BehaviorSubject([] as ICardVerificationForm[]);
  saleTargetSubject = new BehaviorSubject([] as ICardVerificationForm[]);
  documentUpdateSubject = new BehaviorSubject<ICardVerificationDocument>({} as ICardVerificationDocument);

  creditCeilingForm: FormGroup = this.fb.group({
    credit_ceiling_granted: ['', Validators.required],
    sale_target: ['', Validators.required],
    credit_billing: ['', Validators.required],
    distributor_document_change: ['', Validators.required],
  });

  @ViewChild('modalDetailPiutang') modalDetailPiutang: ModalComponent;
  modalDetailPiutangConfig: ModalConfig = {
    modalTitle: 'Detail Tagihan',
    showHeader: false,
    showFooter: false,
  };

  loadingBills = new BehaviorSubject(false);
  billDetailSubject = new BehaviorSubject<IDetailPlafonKredit_CreditCeiling_BillDetails>({} as IDetailPlafonKredit_CreditCeiling_BillDetails);

  @ViewChild('modalConfirmVerification') modalConfirmVerification: ModalComponent;
  modalConfigConfirm = <ModalConfig>{
    modalTitle: 'Konfirmasi Pembaruan Data Distributor',
    dismissButtonLabel: 'Lanjutkan',
    closeButtonLabel: 'Batal',
    onDismiss: () => this.modalConfirmVerification.close().then(() => this.onSubmitVerification()),
  };
  @ViewChild('modalResponseVerification') modalResponseVerification: ModalComponent;
  modalConfigResponse = <ModalConfig>{
    showHeader: true,
    hideCloseButton: () => false,
    dismissButtonLabel: 'Oke',
    closeButtonLabel: 'Lihat Detail',
    disableDismissButton: () => this.loadingSubmit.value,
    disableCloseButton: () => this.loadingSubmit.value,
    onDismiss: () => this.onDismissModalResponse(),
    onClose: () => this.onCloseModalResponse(),
  };

  loadingSubmit = new BehaviorSubject(false);

  get detailHeader() {
    return this.distributorService.DistributorDetailHeader;
  }

  get creditGrantedControl() {
    return <FormControl>this.creditCeilingForm.get('credit_ceiling_granted');
  }

  get creditBillingControl() {
    return <FormControl>this.creditCeilingForm.get('credit_billing');
  }

  get saleTargetControl() {
    return <FormControl>this.creditCeilingForm.get('sale_target');
  }

  get documentUpdateControl() {
    return <FormControl>this.creditCeilingForm.get('distributor_document_change');
  }

  constructor(
    private fb: FormBuilder,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private distributorService: DistributorService,
    private registrationService: DetailRegistrationDistributorService,
    private detailDistributorService: DistributorDetailService,
    private urlParamService: UrlUtilsService,
    public utils: UtilitiesService
  ) {}

  ngOnInit() {
    this.snapshotParams = this.activeRoute.snapshot.params;
    this.getRevisionNoteSession();
    this.getDetailPlafonKredit();
  }

  getDetailPlafonKredit() {
    this.isLoading.next(true);
    this.distributorService.getDetailVerificationPlafonKredit(this.snapshotParams.id).subscribe((res) => {
      if (!res) return;
      this.responseSubject.next(res && res.data);
      this.generateBodyData();
      this.handleValidationForm();
      this.isLoading.next(false);
    });
  }

  generateBodyData() {
    const { credit_ceiling_granted, credit_billing, sale_target, distributor_document_change } = this.responseSubject.value.credit_ceiling;

    this.creditCeilingSubject.next(
      this.registrationService.generateBodyData(credit_ceiling_granted, creditCeilingBodyData(credit_ceiling_granted.current_data, credit_ceiling_granted.revision_data))
    );
    this.creditBillingSubject.next(this.registrationService.generateBodyData(credit_billing, creditBillingBodyData(credit_billing.current_data)));

    const _saleTargetBody = saleTargetBodyData(sale_target.current_data);
    this.saleTargetSubject.next(this.registrationService.generateBodyData(sale_target, _saleTargetBody));

    // mapdocument
    const { revision_note, status_verified_enum, distributor_document_changes } = distributor_document_change;
    const _updateSubject: ICardVerificationDocument = {
      document_changes: distributor_document_changes,
      revision_note: revision_note,
      status_verified_enum: status_verified_enum,
    };

    this.documentUpdateSubject.next(_updateSubject);
  }

  getNoteviewText() {
    if (!Object.keys(this.responseSubject.value).length) return;
    const { request_changes } = this.responseSubject.value;
    if (!request_changes) return 'Informasi tidak tersedia';
    const _dateTime = this.utils.formatEpochToDate(request_changes.date, 'dd-MM-yyyy HH:mm:ss');
    return `Pembaruan data diajukan oleh ${request_changes.actor} pada ${_dateTime}. Data yang diajukan ditandai dengan warna biru, Silakan periksa dan selesaikan proses approval pengajuan data.`;
  }

  checkNoRevision = <T>(data: T) => this.registrationService.checkNotHaveRevision(data);

  getVerifiedStatus = (data: any) => {
    if (!data) return;
    return data && data.status_verified_enum;
  };

  getRevisionNoteSession() {
    this.revisionNoteData = this.registrationService.getSessionRevisionNote(EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE) ?? ({} as IPayloadVerificationPlafonKredit);
  }

  noteValue(type: EnumCardVerificationSection) {
    const { revision_note } = this.revisionNoteData;
    this.handleValidationForm();
    return revision_note[type.toLowerCase() as keyof typeof revision_note];
  }

  onNoteChanges(e: INoteChangeVerification) {
    if (e && e.note === undefined) return;
    this.registrationService.setRevisionData(this.revisionNoteData, e, EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE);
  }

  onTriggerCustomModalButton(e?: any) {
    if (e === undefined) return;
    this.loadingBills.next(true);
    // get legacy detail plafon to extract bill detail data
    this.distributorService.getDetailPlafonKreditLegacy(this.snapshotParams.id).subscribe((res) => {
      if (!res) return;
      this.billDetailSubject.next(res && res.data.credit_ceiling.bill_details);
      this.loadingBills.next(false);
    });

    return this.modalDetailPiutang.open();
  }

  afterActionVerification(e: string | null) {
    if (e === null) this.handleValidationForm();
    return this.urlParamService.reloadComponent();
  }

  handleValidationForm() {
    if (!this.detailDistributorService.detailDataIsLoaded(this.responseSubject.value)) return;

    const _sectionsRevisionStatus = this.sectionsRequestRevision();
    _sectionsRevisionStatus.map((section) => {
      if (!section) return;
      const _key = this.detailDistributorService.sectionKeyAsControlName(section);
      if (_key && section[_key].revision_note === null) this.setFormValidators(false, _key);
    });

    const _sectionsVerifiedStatus = this.sectionsVerified();
    _sectionsVerifiedStatus.map((section) => {
      if (!section) return;
      const _key = this.detailDistributorService.sectionKeyAsControlName(section);
      if (_key) this.setFormValidators(true, _key);
    });

    const _sectionsNoChanges = this.sectionsNoChanges();
    _sectionsNoChanges.map((section) => {
      if (!section) return;
      const _key = this.detailDistributorService.sectionKeyAsControlName(section);
      if (_key) this.setFormValidators(true, _key);
    });

    // set form value
    this.setFormValue();
  }

  setFormValue() {
    const { credit_ceiling_granted, credit_billing, sale_target, document_update_distributor } = this.revisionNoteData.revision_note;
    this.creditGrantedControl.setValue(credit_ceiling_granted);
    this.creditBillingControl.setValue(credit_billing);
    this.saleTargetControl.setValue(sale_target);
    this.documentUpdateControl.setValue(document_update_distributor);
  }

  setFormValidators(clear?: boolean, field?: string) {
    if (field) return clear ? this.creditCeilingForm.controls[field].clearValidators() : this.creditCeilingForm.controls[field].setValidators([Validators.required]);
    Object.keys(this.creditCeilingForm.controls).map((ctrl) =>
      clear ? this.creditCeilingForm.controls[ctrl].clearValidators() : this.creditCeilingForm.controls[ctrl].setValidators([Validators.required])
    );
    this.creditCeilingForm.updateValueAndValidity();
  }

  onSubmitVerification() {
    const _payload = this.revisionNoteData;
    this.loadingSubmit.next(true);
    this.registrationService.submitVerificationFinance(_payload).subscribe((resp) => {
      if (!resp) return;
      this.loadingSubmit.next(false);
      this.removeSessionRevisionNote();
    });
    return this.modalResponseVerification.open();
  }

  onDismissModalResponse() {
    this.router.navigate(['/distributor/pending/edit-request/']).then();
  }

  onCloseModalResponse() {
    const _detailUrl = `/distributor/detail/teregistrasi/active/${this.snapshotParams.id}`;
    this.router.navigate([_detailUrl]).then(() => window.location.reload());
  }

  sectionsRequestRevision() {
    // return section with request revision status
    const { credit_ceiling } = this.responseSubject.value;
    const _res = this.detailDistributorService.filterSectionsByStatus(credit_ceiling, EnumVerificationStatus.REQUEST_REVISION);
    return this.utils.objectMapToArray(_res);
  }

  sectionsVerified() {
    // return section with verified status
    const { credit_ceiling } = this.responseSubject.value;
    const _res = this.detailDistributorService.filterSectionsByStatus(credit_ceiling, EnumVerificationStatus.VERIFIED);
    return this.utils.objectMapToArray(_res);
  }

  sectionsNoChanges() {
    const { credit_ceiling } = this.responseSubject.value;
    const _res = this.detailDistributorService.filterSectionsByStatus(credit_ceiling);
    return this.utils.objectMapToArray(_res);
  }

  hasRevisionNote = () => this.revisionNoteData && Object.values(this.revisionNoteData.revision_note).some((value) => value !== null && value !== '');

  getRevisionDataBody() {
    const { revision_note } = this.revisionNoteData;
    return Object.entries(revision_note)
      .map(([key, value]) => {
        return {
          label: this.utils.mapKeyToString(EnumLabelKeyFormString, key),
          value: value,
        };
      })
      .filter((item) => item.value !== null && item.value !== '');
  }

  removeSessionRevisionNote = (type?: EnumCardVerificationSection) => {
    if (type) return this.registrationService.clearSessionBySection(type, EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE);
    return this.registrationService.clearSessionRevisionNote(EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE);
  };

  showButtonConfirm() {
    return this.registrationService.checkBtnVerification(this.snapshotParams.tab);
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly REVISION_SESSION = EnumDistributorRevisionSession;

  // todo: put on helper or utils file
  // isObject(data: any) {
  //   return typeof !!(data && data === 'object') && !Array.isArray(data);
  // }
  //
  // removeEmptyObjectProps<T>(data: T) {
  //   if (!this.isObject(data)) return data;
  //   let _newObj = {} as T;
  //   Object.keys(data as Object).forEach((key) => {
  //     const _k = key as keyof typeof data;
  //     if (data[_k] !== null && data[_k] !== '' && data[_k] !== undefined) _newObj[_k] = data[_k];
  //   });
  //   return _newObj;
  // }
}
